import { type CtiInteractionTypeEnum, type CtiStatus } from '@/cti/types/cti'
import { type Ref } from 'vue'

export enum VoiceProvider {
  'UNIFIED_CTI' = 'unified-cti',
  'AXIALYS' = 'axialys',
  'GENESYS' = 'genesys',
  'VOICEBOT' = 'voicebot',
  'FIVE_9' = 'five9',
  'NONE' = 'none',
}

export enum VoiceConfigurationTabEnum {
  'GENERAL' = 'general',
  'INBOUND' = 'inbound',
  'OUTBOUND' = 'outbound',
}

export enum ProviderConnectionType {
  'TOKEN' = 'token',
  'MANUAL' = 'manual',
  'SSO' = 'SSO',
}

export enum ProviderUIType {
  'FRAMED' = 'framed',
  'SIDE' = 'side',
}

export enum LandingPageEnum {
  'TICKET_HANDLING_SCREEN' = 'ticket-handling-screen',
  'SEARCH_CUSTOMER_SCREEN' = 'search-customer-screen',
}

export enum LandingPageForVoicebotOnly {
  'NO_HANDLING_SCREEN' = 'no-handling-screen',
}

export enum CustomerPhoneFieldEnum {
  'HOMEPHONE' = 'homePhone',
  'WORKPHONE' = 'workPhone',
  'MOBILEPHONE' = 'mobilePhone',
  'OTHERPHONE' = 'otherPhone',
}

export type CallConfig = {
  configType: VoiceConfigurationTabEnum
  callConfig?: InboundCallConfigurationDTOV2 | OutboundCallConfigurationDTOV2
  configId?: UUID
}

export type VoiceConfigurationDTOV1 = {
  activeProvider: VoiceProvider
}

export type VoiceConfigurationDTOV2 = {
  defaultProvider: VoiceProvider
}

export type ProviderGeneralConfigurationDTOV2 = {
  id: UUID | null
  provider: VoiceProvider
  providerConfiguration?: ProviderSpecificConfiguration | null
  connectionType: ProviderConnectionType | null
  connection?: {
    login?: string
    password?: string
  }
  uiIntegrationType?: ProviderUIType
  landingPage: LandingPageEnum | LandingPageForVoicebotOnly | null
  fallbackSkill: string | null
  iframeUrl?: string
  creationCustomerWhenNotFoundEnabled: boolean
  attachLatestCustomerWhenMultipleFoundEnabled?: boolean
  creationCustomerPhoneFieldName: CustomerPhoneFieldEnum | null
  space: UUID
}

type ProviderSpecificConfiguration = {
  genesys?: GenesysIFrameConfiguration
  axialys?: AxialysConfigurationDTOV2
  unifiedCti?: UnifiedCtiIFrameConfigurationDTOV2
  five9?: Five9ConfigurationDTOV2
}
export interface AxialysConfigurationDTOV2 {
  login?: string
  password?: string
}

export interface GenesysIFrameConfiguration {
  clientId: string
  apiUrl: string
  loginUrl: string
}

// eslint-disable-next-line @typescript-eslint/no-empty-object-type
export interface UnifiedCtiIFrameConfigurationDTOV2 {}

export interface Five9ConfigurationDTOV2 {
  sdkUrl: string
  customerRefName: string
  customerRefGroup: string
}

export type CallConfigurationDTOV2 = {
  id?: UUID
  providerConfiguration: UUID
  summary?: string
  code: string
  phoneNumber?: string
  space?: UUID
  active?: boolean
}

export type InboundCallConfigurationDTOV2 = {
  skill: string
} & CallConfigurationDTOV2

export type OutboundCallConfigurationDTOV2 = {
  defaultSkill: string
  requiredSkills?: Array<string>
} & CallConfigurationDTOV2

export interface CallDTOV1 {
  callId: string
  provider: VoiceProvider
  groupId: string | null
  recordingLink: string | null
  serviceNumber: string | null
  callConfiguration: UUID
  customer: VoiceCustomer
  type: CtiInteractionTypeEnum
  ticketId: number
}

export interface CallInteractionDTOV2 {
  callId: string | null
  providerConfiguration: UUID
  callConfiguration?: UUID
  customer?: VoiceCustomer
  agent?: VoiceAgent
  escalateToLiveAgent?: boolean
  duration?: string
  messages?: CallMessage[]
  caseId?: number
  type: CtiInteractionTypeEnum
}

export interface CallMessage {
  text: string
  type: CtiInteractionTypeEnum
}

export interface CallRequestBody {
  callId: string | null
  provider: VoiceProvider
  serviceNumber: string | null
  callConfiguration: UUID | null | undefined
  groupId: string | null
  customer: VoiceCustomer
  type: CtiInteractionTypeEnum
  agent: VoiceAgent
  ticketId?: number | null
}

export interface OutboundCallRequestBody {
  outboundCallNumberId: UUID
  phoneNumber: string
  ticketId: number | null
  customerId: number | null
}

export interface CallDurationUpdateDTOV1 {
  provider: string
  duration: CallDuration
}

export interface PatchCallInteractionDTOV2 {
  recordingLink?: string
  duration: CallDuration
}

type DateUnit = `${number}Y` | `${number}M` | `${number}D`
type TimeUnit = `${number}H` | `${number}M` | `${number}S`

type DatePart = DateUnit | `${DateUnit}${DateUnit}` | `${DateUnit}${DateUnit}${DateUnit}`
type TimePart = TimeUnit | `${TimeUnit}${TimeUnit}` | `${TimeUnit}${TimeUnit}${TimeUnit}`

export type CallDuration = `P${number}W` | `P${DatePart}` | `P${DatePart}T${TimePart}` | `PT${TimePart}`

export interface VoiceCustomer {
  customerNumber: string | null
  userToUserInformation?: string
  externalReference?: string | null
  customerId?: number | null
  foundMultipleCustomers?: boolean | null
}

export interface VoiceAgent {
  ctiLogin?: string | null
  extension?: string
  desktopLogin?: string
}

export interface AgentModel {
  providerLogin?: string | null
  extension?: string
  desktopLogin?: string
  status?: CtiStatus
}

export type ModalConfirm = {
  configType: string
  status: Ref<boolean>
  config: InboundCallConfigurationDTOV2 | OutboundCallConfigurationDTOV2
}

export interface IFrameStyle {
  narrowWidth: string
  wideWidth: string
  height: string
  innerPadding: string
}

export interface CtiCallInfo {
  requestInfo: CallRequestBody | CallInteractionDTOV2 | null
  isCallV2: boolean
  customerResultLength: number
}
